// File: src/components/SessionPanel/hooks/usePrefill.ts
import { useEffect, useRef } from "react";
import type { UpdateEvent } from "../../../types/domain";

/** Idempotently applies prefill summary/events when a historical session is selected. */
export function usePrefill(opts: {
  selectedSessionId?: string;
  prefillSummary?: string;
  prefillEvents?: UpdateEvent[];
  summary: string;
  setSummary: (s: string) => void;
  onSummary?: (s: string) => void;
  events: UpdateEvent[];
  setEvents: (e: UpdateEvent[]) => void;
  seenSeqsRef: React.MutableRefObject<Set<number>>;
  afterSeqRef: React.MutableRefObject<number>;
  setLatestSeq: (n: number) => void;
}) {
  const {
    selectedSessionId,
    prefillSummary,
    prefillEvents,
    summary,
    setSummary,
    onSummary,
    events,
    setEvents,
    seenSeqsRef,
    afterSeqRef,
    setLatestSeq,
  } = opts;

  const lastAppliedForRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!selectedSessionId || selectedSessionId === "current") return;

    const shouldApplyBecauseNewSession = lastAppliedForRef.current !== selectedSessionId;
    const shouldApplyBecauseWiped =
      (!summary || summary.trim() === "") && (prefillSummary ?? "").trim().length > 0;

    if (shouldApplyBecauseNewSession || shouldApplyBecauseWiped) {
      const incoming = (prefillSummary ?? "").trim();
      setSummary(incoming);
      onSummary?.(incoming);
      lastAppliedForRef.current = selectedSessionId;
    }
  }, [selectedSessionId, prefillSummary, summary, setSummary, onSummary]);

  const eventsAppliedForRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!selectedSessionId || selectedSessionId === "current") return;

    const haveLocalEvents = Array.isArray(events) && events.length > 0;
    const havePrefill = Array.isArray(prefillEvents) && prefillEvents.length > 0;

    const shouldApplyBecauseNewSession = eventsAppliedForRef.current !== selectedSessionId;
    const shouldApplyBecauseCleared = !haveLocalEvents && havePrefill;

    if ((shouldApplyBecauseNewSession || shouldApplyBecauseCleared) && havePrefill) {
      const normalized = prefillEvents!
        .filter((e) => e && typeof e.text === "string" && e.text.trim().length > 0)
        .map((e, i) => ({
          seq: typeof e.seq === "number" ? e.seq : i + 1,
          ts: typeof e.ts === "number" ? e.ts : undefined,
          start_sec: typeof e.start_sec === "number" ? e.start_sec : undefined,
          end_sec: typeof e.end_sec === "number" ? e.end_sec : undefined,
          speaker: typeof e.speaker === "string" ? e.speaker : "?",
          text: (e.text ?? "").trim(),
        })) as UpdateEvent[];

      normalized.sort((a, b) => (a.seq || 0) - (b.seq || 0));

      seenSeqsRef.current = new Set<number>(normalized.map((e) => e.seq));
      afterSeqRef.current = normalized.reduce((m, e) => Math.max(m, e.seq || 0), 0);

      const maxEndSec = normalized.reduce((m, e: any) => typeof e?.end_sec === "number" && e.end_sec > m ? e.end_sec : m, 0);
      console.log("[DBG usePrefill] APPLY sessionId=", selectedSessionId, "count=", normalized.length, "maxEndSec=", maxEndSec, "reason=", shouldApplyBecauseNewSession ? "newSession" : "cleared");
      setEvents(normalized);
      setLatestSeq(afterSeqRef.current);

      eventsAppliedForRef.current = selectedSessionId;
    }
  }, [selectedSessionId, prefillEvents, events, setEvents, setLatestSeq, seenSeqsRef, afterSeqRef]);
}
